import React, { useEffect, useState } from 'react';
import { Grid, Box, CircularProgress, Paper, IconButton } from "@mui/material";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DescriptionIcon from '@mui/icons-material/Description';
import { exists, getFile, saveFile } from '../utils/indexedDb';
import { handleError } from '../utils/lib';
import { useSnackbar } from "../providers/SnackBarProvider";
import { useTranslation } from 'react-i18next';

const ResourcesList = ({ resources }) => {
  const { t } = useTranslation();
  const { snackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [localResources, setLocalResources] = useState([]);

  useEffect(() => {
    const checkLocalResources = async () => {
      const updatedResources = await Promise.all(
        resources.map(async (resource) => {
          const existsLocally = await exists(resource.file_name);
          return {
            ...resource,
            existsLocally,
            localUrl: existsLocally ? await getFile(resource.file_name) : null
          };
        })
      );
      setLocalResources(updatedResources);
      setIsLoading(false);
    };

    checkLocalResources();
  }, [resources]);

  const handleDownload = async (resource) => {
    setDownloading(true);
    try {
      const response = await fetch(resource.file_url);
      //console.log(response);
      const blob = await response.blob();
      await saveFile(resource.file_name, blob);
      
      // Update local resources state
      setLocalResources(prev => 
        prev.map(r => 
          r.unique_id === resource.unique_id 
            ? { ...r, existsLocally: true, localUrl: blob }
            : r
        )
      );
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setDownloading(false);
    }
  };

  const handleFileClick = (resource) => {
    if (resource.existsLocally) {
      const url = URL.createObjectURL(resource.localUrl);
      window.open(url, '_blank');
    }
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ fontSize: 40, color: 'error.main' }} />;
      case 'xlsx':
        return <TableChartIcon sx={{ fontSize: 40, color: 'success.main' }} />;
      case 'docx':
        return <DescriptionIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
      default:
        return <AttachFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />;
    }
  };

  if (!resources || resources.length === 0) return null;

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
      <CircularProgress />
    </Box>
  );
  
  return (
    <Box sx={{ mt: 2 }}>
      {/* <Typography variant="subtitle2" sx={{ mb: 1 }}>Archivos adjuntos:</Typography> */}
      <Grid container spacing={2}>
        {localResources.map((resource, index) => (
          <Grid item xs={4} key={index}>
            <Paper 
              sx={{ 
                position: 'relative',
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                cursor: resource.existsLocally ? 'pointer' : 'default'
              }}
              onClick={() => resource.existsLocally && handleFileClick(resource)}
            >
              {resource.existsLocally ? (
                // Show file preview based on type
                resource.file_type === "IMAGE" ? (
                  <img 
                    src={URL.createObjectURL(resource.localUrl)} 
                    alt={resource.file_name}
                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : resource.file_type === "VIDEO" ? (
                  <video
                    controls
                    style={{ width: "100%", borderRadius: 8 }}
                  >
                    <source src={URL.createObjectURL(resource.localUrl)} type="video/mp4" />
                    {t('layouts.browserNoVideo')}
                  </video>
                ) : resource.file_type === "AUDIO" ? (
                  <audio
                    controls
                    style={{ width: "100%" }}
                  >
                    <source src={URL.createObjectURL(resource.localUrl)} type="audio/mpeg" />
                    {t('layouts.browserNoAudio')}
                  </audio>
                ) : (
                  getFileIcon(resource.file_name)
                )
              ) : (
                <CloudDownloadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
              )}

              {!resource.existsLocally && (
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(255, 255, 255, 0.8)'
                  }}
                  onClick={() => !resource.existsLocally && handleDownload(resource)}
                  disabled={downloading || resource.existsLocally}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              )}
            </Paper>
            {/* <Typography 
              variant="caption" 
              sx={{ 
                display: 'block',
                textAlign: 'center',
                mt: 0.5,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {resource.file_name}
            </Typography> */}
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ResourcesList;
